import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { fetchSlokasByLanguage } from '../api/client';
import { buildPlaybackQueue, getAdjacentChapterInQueue, findChapterStartIndex, getChaptersInQueue } from '../utils/buildPlaybackQueue';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';
import { useAudioVoice } from './AudioVoiceContext';

const ContinuousPlayerContext = createContext(null);

const SETTINGS_KEY = 'gita-player-settings';
const SPEED_KEY = 'gita-playback-speed';
const VALID_SPEEDS = [0.75, 1, 1.25, 1.5];

function readStoredSpeed() {
  try {
    const v = parseFloat(localStorage.getItem(SPEED_KEY));
    return VALID_SPEEDS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

const DEFAULT_SETTINGS = {
  mode: 'sloka',
  language: DEFAULT_LANGUAGE,
  startChapter: 1,
  endChapter: 18,
  repeat: 'none'
};

function readStoredSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      mode: ['sloka', 'meaning', 'both'].includes(parsed.mode) ? parsed.mode : DEFAULT_SETTINGS.mode,
      language: typeof parsed.language === 'string' ? parsed.language : DEFAULT_SETTINGS.language,
      startChapter: Number.isInteger(parsed.startChapter) ? parsed.startChapter : DEFAULT_SETTINGS.startChapter,
      endChapter: Number.isInteger(parsed.endChapter) ? parsed.endChapter : DEFAULT_SETTINGS.endChapter,
      repeat: ['none', 'all'].includes(parsed.repeat) ? parsed.repeat : DEFAULT_SETTINGS.repeat,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function writeStoredSettings(s) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // Blocked in private mode or by browser settings.
  }
}

export function ContinuousPlayerProvider({ children }) {
  const { voice } = useAudioVoice();
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const settingsRef = useRef(DEFAULT_SETTINGS);

  const [settings, setSettings] = useState(readStoredSettings);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playerVariant, setPlayerVariant] = useState('apple');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeedState] = useState(readStoredSpeed);

  settingsRef.current = settings;
  queueRef.current = queue;
  indexRef.current = currentIndex;

  useEffect(() => {
    writeStoredSettings(settings);
  }, [settings]);

  const currentTrack = queue[currentIndex] || null;

  const playTrackAt = useCallback((index, tracks) => {
    const list = tracks || queueRef.current;
    if (!audioRef.current || !list[index]) return;

    const audio = audioRef.current;
    setCurrentIndex(index);
    indexRef.current = index;
    setCurrentTime(0);
    setDuration(0);
    audio.src = list[index].src;
    audio.load();

    const applyDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener('loadedmetadata', applyDuration, { once: true });
    audio.addEventListener('durationchange', applyDuration, { once: true });

    audio.play().catch(() => setIsPlaying(false));
    setIsPlaying(true);
  }, []);

  const buildQueueFromSettings = useCallback(async (nextSettings, selectedVoice) => {
    const slokas = await fetchSlokasByLanguage(nextSettings.language);
    return buildPlaybackQueue(slokas, { ...nextSettings, voice: selectedVoice });
  }, []);

  const startPlayback = useCallback(async (overrides = {}) => {
    const { variant, ...settingsOverrides } = overrides;
    if (variant) setPlayerVariant(variant);

    const nextSettings = { ...settingsRef.current, ...settingsOverrides };
    setSettings(nextSettings);
    setIsLoading(true);
    setError(null);

    try {
      const newQueue = await buildQueueFromSettings(nextSettings, voice);

      if (newQueue.length === 0) {
        throw new Error('No audio tracks found for the selected options.');
      }

      setQueue(newQueue);
      queueRef.current = newQueue;
      playTrackAt(0, newQueue);
    } catch (err) {
      setError(err.message || 'Failed to start playback.');
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [buildQueueFromSettings, playTrackAt, voice]);

  const togglePlay = useCallback(async () => {
    if (queueRef.current.length === 0) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    if (audioRef.current?.src) {
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
      return;
    }

    playTrackAt(indexRef.current);
  }, [isPlaying, playTrackAt]);

  const playNext = useCallback(() => {
    const list = queueRef.current;
    if (list.length === 0) return;

    const nextIndex = indexRef.current + 1;
    if (nextIndex < list.length) {
      playTrackAt(nextIndex);
      return;
    }

    if (settingsRef.current.repeat === 'all') {
      playTrackAt(0);
      return;
    }

    audioRef.current?.pause();
    setIsPlaying(false);
  }, [playTrackAt]);

  const playPrevious = useCallback(() => {
    const list = queueRef.current;
    if (list.length === 0) return;

    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }

    const prevIndex = indexRef.current - 1;
    if (prevIndex >= 0) {
      playTrackAt(prevIndex);
    }
  }, [playTrackAt]);

  const playNextChapter = useCallback(() => {
    const list = queueRef.current;
    const current = list[indexRef.current];
    if (!current) return;

    let targetChapter = getAdjacentChapterInQueue(list, current.chapterNumber, 1);
    if (targetChapter == null && settingsRef.current.repeat === 'all') {
      const chapters = getChaptersInQueue(list);
      targetChapter = chapters[0] ?? null;
    }
    if (targetChapter == null) return;

    const targetIndex = findChapterStartIndex(list, targetChapter);
    if (targetIndex >= 0) playTrackAt(targetIndex);
  }, [playTrackAt]);

  const playPreviousChapter = useCallback(() => {
    const list = queueRef.current;
    const current = list[indexRef.current];
    if (!current) return;

    const targetChapter = getAdjacentChapterInQueue(list, current.chapterNumber, -1);
    if (targetChapter == null) return;

    const targetIndex = findChapterStartIndex(list, targetChapter);
    if (targetIndex >= 0) playTrackAt(targetIndex);
  }, [playTrackAt]);

  const canPlayNextChapter =
    queue.length > 0 &&
    currentTrack != null &&
    (getAdjacentChapterInQueue(queue, currentTrack.chapterNumber, 1) != null ||
      settings.repeat === 'all');

  const canPlayPreviousChapter =
    queue.length > 0 &&
    currentTrack != null &&
    getAdjacentChapterInQueue(queue, currentTrack.chapterNumber, -1) != null;

  const jumpToTrack = useCallback(
    (index) => {
      if (index >= 0 && index < queueRef.current.length) {
        playTrackAt(index);
      }
    },
    [playTrackAt]
  );

  const setSpeed = useCallback((v) => {
    if (!VALID_SPEEDS.includes(v)) return;
    setSpeedState(v);
    try { localStorage.setItem(SPEED_KEY, String(v)); } catch {}
    if (audioRef.current) audioRef.current.playbackRate = v;
  }, []);

  // Re-apply speed after each track load (playbackRate survives load() but be explicit)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const apply = () => { audio.playbackRate = speed; };
    audio.addEventListener('loadedmetadata', apply);
    return () => audio.removeEventListener('loadedmetadata', apply);
  }, [speed]);

  const seek = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(time)) return;
    audio.currentTime = Math.max(0, Math.min(time, audio.duration || time));
    setCurrentTime(audio.currentTime);
  }, []);

  const toggleRepeat = useCallback(() => {
    setSettings((prev) => {
      const next = { ...prev, repeat: prev.repeat === 'all' ? 'none' : 'all' };
      settingsRef.current = next;
      return next;
    });
  }, []);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsPlaying(false);
    setQueue([]);
    queueRef.current = [];
    setCurrentIndex(0);
    indexRef.current = 0;
    setCurrentTime(0);
    setDuration(0);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const syncProgress = () => {
      setCurrentTime(audio.currentTime);
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const onEnded = () => playNext();
    const onError = () => { setTimeout(() => playNext(), 1500); };

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('timeupdate', syncProgress);
    audio.addEventListener('loadedmetadata', syncProgress);
    audio.addEventListener('durationchange', syncProgress);
    audio.addEventListener('playing', syncProgress);
    audio.addEventListener('seeked', syncProgress);

    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('timeupdate', syncProgress);
      audio.removeEventListener('loadedmetadata', syncProgress);
      audio.removeEventListener('durationchange', syncProgress);
      audio.removeEventListener('playing', syncProgress);
      audio.removeEventListener('seeked', syncProgress);
    };
  }, [playNext]);

  // Smooth live transcript updates while audio plays (timeupdate alone is too sparse)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isPlaying) return undefined;

    let frameId = 0;

    const tick = () => {
      setCurrentTime(audio.currentTime);
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frameId);
  }, [isPlaying, currentIndex]);

  useEffect(() => {
    if (queue.length === 0) return undefined;

    let cancelled = false;

    (async () => {
      const trackId = queue[indexRef.current]?.id;
      const rebuilt = await buildQueueFromSettings(settingsRef.current, voice);
      if (cancelled || rebuilt.length === 0) return;

      const newIndex = trackId ? rebuilt.findIndex((t) => t.id === trackId) : 0;
      const safeIndex = newIndex >= 0 ? newIndex : 0;

      setQueue(rebuilt);
      queueRef.current = rebuilt;
      setCurrentIndex(safeIndex);
      indexRef.current = safeIndex;

      if (isPlaying && audioRef.current) {
        audioRef.current.src = rebuilt[safeIndex].src;
        audioRef.current.load();
        audioRef.current.play().catch(() => setIsPlaying(false));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice]);

  const value = {
    settings,
    setSettings,
    queue,
    currentIndex,
    currentTrack,
    isPlaying,
    isLoading,
    error,
    playerVariant,
    currentTime,
    duration,
    startPlayback,
    togglePlay,
    playNext,
    playPrevious,
    playNextChapter,
    playPreviousChapter,
    canPlayNextChapter,
    canPlayPreviousChapter,
    jumpToTrack,
    seek,
    toggleRepeat,
    stopPlayback,
    speed,
    setSpeed,
    totalTracks: queue.length
  };

  return (
    <ContinuousPlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="auto" className="hidden" aria-hidden="true" />
    </ContinuousPlayerContext.Provider>
  );
}

export function useContinuousPlayer() {
  const ctx = useContext(ContinuousPlayerContext);
  if (!ctx) {
    throw new Error('useContinuousPlayer must be used within ContinuousPlayerProvider');
  }
  return ctx;
}
